import React, { useEffect, useState, useRef } from "react";
import { X, Play, ArrowCounterClockwise, CheckCircle } from "@phosphor-icons/react";
import api from "../../lib/api";

const TOTAL = 25;
const GO_PROB = 0.7;  // 70% go, 30% no-go
const STIM_MS = 700;  // tiempo visible del estímulo
const ITI_MS = 600;   // intervalo entre estímulos

/**
 * Go/No-Go: cada ronda aparece un círculo verde (Go) o rojo (No-Go).
 * El atleta toca lo más rápido que pueda SI ES VERDE, y NO toca si es ROJO.
 *
 * Mide reaction time en Go correctos + tasa de comisiones (tap en No-Go,
 * típico marcador de impulsividad).
 */
export default function GoNoGoPlayer({ onClose }) {
  const [phase, setPhase] = useState("intro");
  const [trial, setTrial] = useState(0);
  const [stimulus, setStimulus] = useState(null); // 'go' | 'nogo' | null
  const [hits, setHits] = useState(0);            // tap en Go (correcto)
  const [omissions, setOmissions] = useState(0);  // no tap en Go (perdido)
  const [commissions, setCommissions] = useState(0); // tap en No-Go (error)
  const [rts, setRts] = useState([]);
  const tappedRef = useRef(false);
  const stimAtRef = useRef(null);
  const stimTypeRef = useRef(null);
  const startedAtRef = useRef(null);
  const cancelledRef = useRef(false);

  const start = () => {
    startedAtRef.current = new Date();
    setTrial(0); setHits(0); setOmissions(0); setCommissions(0); setRts([]);
    setPhase("playing");
  };

  useEffect(() => {
    if (phase !== "playing") return;
    cancelledRef.current = false;
    let timer = null;

    const run = async () => {
      for (let i = 0; i < TOTAL; i++) {
        if (cancelledRef.current) return;
        setTrial(i);
        const type = Math.random() < GO_PROB ? "go" : "nogo";
        stimTypeRef.current = type;
        tappedRef.current = false;
        stimAtRef.current = performance.now();
        setStimulus(type);
        await new Promise((res) => { timer = setTimeout(res, STIM_MS); });
        if (cancelledRef.current) return;
        // Evaluar
        if (type === "go" && !tappedRef.current) {
          setOmissions((o) => o + 1);
        }
        setStimulus(null);
        if (i < TOTAL - 1) {
          await new Promise((res) => { timer = setTimeout(res, ITI_MS); });
        }
      }
      if (!cancelledRef.current) setPhase("done");
    };
    run();
    return () => { cancelledRef.current = true; if (timer) clearTimeout(timer); };
  }, [phase]);

  const handleTap = () => {
    if (phase !== "playing") return;
    if (!stimulus || tappedRef.current) return;
    tappedRef.current = true;
    const rt = performance.now() - stimAtRef.current;
    if (stimTypeRef.current === "go") {
      setHits((h) => h + 1);
      setRts((arr) => [...arr, rt]);
    } else {
      setCommissions((c) => c + 1);
    }
  };

  useEffect(() => {
    if (phase !== "done" || !startedAtRef.current) return;
    const avg = rts.length ? rts.reduce((a, b) => a + b, 0) / rts.length : null;
    api.post("/neuroscience/results", {
      config_id: "default_go_nogo",
      started_at: startedAtRef.current.toISOString(),
      finished_at: new Date().toISOString(),
      total_taps: hits + commissions,
      correct_taps: hits,
      avg_reaction_ms: avg,
      notes: `Go/No-Go · Hits ${hits} · Omisiones ${omissions} · Comisiones ${commissions}`,
    }).catch(() => {});
  }, [phase, hits, commissions, omissions, rts]);

  const avgMs = rts.length ? Math.round(rts.reduce((a, b) => a + b, 0) / rts.length) : null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-app flex flex-col items-center justify-center p-6"
      onClick={handleTap}
      data-testid="gonogo-player"
    >
      <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="absolute top-3 right-3 w-10 h-10 flex items-center justify-center bg-black/60 text-white z-10" data-testid="gonogo-close">
        <X size={18} weight="bold" />
      </button>

      {phase === "intro" && (
        <div className="text-center max-w-md text-white" onClick={(e) => e.stopPropagation()}>
          <div className="font-display font-black uppercase text-3xl tracking-tighter mb-3">Go / No-Go</div>
          <p className="text-muted text-sm mb-2 leading-relaxed">
            Va a aparecer un círculo en pantalla. Si es <strong className="text-[#32D74B]">verde (GO)</strong>, tocá lo más rápido que puedas. Si es <strong className="text-[#FF3B30]">rojo (NO-GO)</strong>, NO toques.
          </p>
          <p className="text-muted text-xs mb-6">{TOTAL} estímulos · mide reacción + control de impulsos.</p>
          <button onClick={(e) => { e.stopPropagation(); start(); }} className="btn-primary w-full !py-4 flex items-center justify-center gap-2" data-testid="gonogo-start">
            <Play size={20} weight="fill" /> Empezar
          </button>
        </div>
      )}

      {phase === "playing" && (
        <>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-xs uppercase tracking-widest pointer-events-none">
            {trial + 1}/{TOTAL} · Hits {hits} · Comisiones <span className="text-[#FF3B30]">{commissions}</span>
          </div>
          <div
            className="rounded-full transition-all duration-100"
            style={{
              width: stimulus ? 220 : 80,
              height: stimulus ? 220 : 80,
              backgroundColor: stimulus === "go" ? "#32D74B" : stimulus === "nogo" ? "#FF3B30" : "#1a1a1a",
              border: stimulus ? "4px solid #fff" : "2px solid #262626",
            }}
            data-testid={`gonogo-stim-${stimulus || "none"}`}
          />
        </>
      )}

      {phase === "done" && (
        <div className="text-center max-w-md text-white" onClick={(e) => e.stopPropagation()}>
          <CheckCircle size={48} weight="duotone" className="text-[#32D74B] mx-auto mb-4" />
          <div className="font-display font-black uppercase text-3xl tracking-tighter mb-4">¡Listo!</div>
          <div className="bg-[#141414] border border-app p-4 mb-6 grid grid-cols-2 gap-3 text-center">
            <div><div className="text-[10px] uppercase tracking-widest text-muted">Hits (Go ✓)</div><div className="font-display font-black text-2xl text-[#32D74B] mt-1">{hits}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-muted">Omisiones</div><div className="font-display font-black text-2xl mt-1">{omissions}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-muted">Comisiones</div><div className="font-display font-black text-2xl text-[#FF3B30] mt-1">{commissions}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-muted">RT prom</div><div className="font-display font-black text-2xl text-primary-app mt-1">{avgMs ? `${avgMs}ms` : "—"}</div></div>
          </div>
          <div className="flex gap-3">
            <button onClick={(e) => { e.stopPropagation(); start(); }} className="btn-secondary flex-1 flex items-center justify-center gap-2" data-testid="gonogo-replay">
              <ArrowCounterClockwise size={16} weight="bold" /> Otra vez
            </button>
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="btn-primary flex-1" data-testid="gonogo-finish">Terminar</button>
          </div>
        </div>
      )}
    </div>
  );
}
